"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Destination = exports.fieldsToJsonSchema = exports.hookTypeStrings = void 0;
const destination_subscriptions_1 = require("@segment/destination-subscriptions");
const action_1 = require("./action");
Object.defineProperty(exports, "hookTypeStrings", { enumerable: true, get: function () { return action_1.hookTypeStrings; } });
const time_1 = require("../time");
const fields_to_jsonschema_1 = require("./fields-to-jsonschema");
Object.defineProperty(exports, "fieldsToJsonSchema", { enumerable: true, get: function () { return fields_to_jsonschema_1.fieldsToJsonSchema; } });
const create_request_client_1 = __importDefault(require("../create-request-client"));
const schema_validation_1 = require("../schema-validation");
const errors_1 = require("../errors");
const parse_settings_1 = require("./parse-settings");
const retry_1 = require("../retry");
const instanceOfAudienceDestinationSettingsWithCreateGet = (object) => {
    return 'createAudience' in object && 'getAudience' in object;
};
class Destination {
    constructor(destination) {
        this.definition = destination;
        this.name = destination.name;
        this.extendRequest = destination.extendRequest;
        this.actions = {};
        this.authentication = destination.authentication;
        this.responses = [];
        if (this.definition.onDelete) {
            this.onDelete = this._onDelete;
        }
        if (this.authentication?.fields) {
            this.settingsSchema = (0, fields_to_jsonschema_1.fieldsToJsonSchema)(this.authentication.fields);
        }
        for (const action of Object.keys(destination.actions)) {
            this.partnerAction(action, destination.actions[action]);
        }
    }
    validateSettings(settings) {
        if (this.settingsSchema) {
            try {
                (0, schema_validation_1.validateSchema)(settings, this.settingsSchema, {
                    schemaKey: `${this.name}:settings`,
                    exempt: ['dynamicAuthSettings']
                });
            }
            catch (err) {
                const error = err;
                if (error.name === 'AggregateAjvError' || error.name === 'ValidationError') {
                    error.status = 400;
                }
                throw error;
            }
        }
    }
    async createAudience(createAudienceInput) {
        let settings = createAudienceInput.settings;
        const { audienceConfig, audienceFields } = this.definition;
        if (!instanceOfAudienceDestinationSettingsWithCreateGet(audienceConfig)) {
            throw new Error('Unexpected call to createAudience');
        }
        if (createAudienceInput.audienceSettings && Object.keys(createAudienceInput.audienceSettings).length > 0) {
            (0, schema_validation_1.validateSchema)(createAudienceInput.audienceSettings, (0, fields_to_jsonschema_1.fieldsToJsonSchema)(audienceFields), {
                exempt: ['dynamicAuthSettings']
            });
        }
        const destinationSettings = this.getDestinationSettings(settings);
        const run = async () => {
            const auth = (0, parse_settings_1.getAuthData)(settings);
            const context = {
                audienceSettings: createAudienceInput.audienceSettings,
                settings: destinationSettings,
                payload: undefined,
                auth
            };
            const opts = this.extendRequest?.(context) ?? {};
            const requestClient = (0, create_request_client_1.default)({ ...opts, statsContext: context.statsContext });
            return await audienceConfig?.createAudience(requestClient, createAudienceInput);
        };
        const onFailedAttempt = async (error) => {
            settings = await this.handleError(error, settings);
        };
        return await (0, retry_1.retry)(run, { retries: 2, onFailedAttempt });
    }
    async getAudience(getAudienceInput) {
        const { audienceConfig } = this.definition;
        let settings = getAudienceInput.settings;
        if (!instanceOfAudienceDestinationSettingsWithCreateGet(audienceConfig)) {
            throw new Error('Unexpected call to getAudience');
        }
        const destinationSettings = this.getDestinationSettings(settings);
        const run = async () => {
            const auth = (0, parse_settings_1.getAuthData)(settings);
            const context = {
                audienceSettings: getAudienceInput.audienceSettings,
                settings: destinationSettings,
                payload: undefined,
                auth
            };
            const opts = this.extendRequest?.(context) ?? {};
            const requestClient = (0, create_request_client_1.default)({ ...opts, statsContext: context.statsContext });
            return await audienceConfig?.getAudience(requestClient, getAudienceInput);
        };
        const onFailedAttempt = async (error) => {
            settings = await this.handleError(error, settings);
        };
        return await (0, retry_1.retry)(run, { retries: 2, onFailedAttempt });
    }
    async testAuthentication(settings) {
        const destinationSettings = this.getDestinationSettings(settings);
        const auth = (0, parse_settings_1.getAuthData)(settings);
        const data = { settings: destinationSettings, auth };
        const context = {
            settings: destinationSettings,
            payload: undefined,
            auth
        };
        this.validateSettings(destinationSettings);
        if (!this.authentication?.testAuthentication) {
            return;
        }
        const options = this.extendRequest?.(context) ?? {};
        const requestClient = (0, create_request_client_1.default)({ ...options, statsContext: context.statsContext });
        try {
            await this.authentication.testAuthentication(requestClient, data);
        }
        catch (error) {
            const typedError = error;
            const statusCode = typedError?.response?.status ?? '';
            throw new Error(`Credentials are invalid: ${statusCode} ${typedError.message}`);
        }
    }
    async refreshAccessToken(settings, oauthData, synchronizeRefreshAccessToken) {
        if (!(this.authentication?.scheme === 'oauth2' || this.authentication?.scheme === 'oauth-managed')) {
            throw new errors_1.IntegrationError('refreshAccessToken is only valid with oauth2 authentication scheme', 'NotImplemented', 501);
        }
        const context = {
            settings,
            payload: undefined,
            auth: (0, parse_settings_1.getAuthData)(settings)
        };
        const options = this.extendRequest?.(context) ?? {};
        const requestClient = (0, create_request_client_1.default)({ ...options, statsContext: context.statsContext });
        if (!this.authentication?.refreshAccessToken) {
            return undefined;
        }
        await synchronizeRefreshAccessToken?.();
        return this.authentication.refreshAccessToken(requestClient, { settings, auth: oauthData });
    }
    partnerAction(slug, definition) {
        const action = new action_1.Action(this.name, definition, this.extendRequest);
        action.on('response', (response) => {
            if (response) {
                this.responses.push(response);
            }
        });
        this.actions[slug] = action;
        return this;
    }
    async executeAction(actionSlug, { event, mapping, subscriptionMetadata, settings, auth, features, statsContext, logger, engageDestinationCache, transactionContext, stateContext }) {
        const action = this.actions[actionSlug];
        if (!action) {
            return [];
        }
        let audienceSettings = {};
        if (event.context?.personas) {
            audienceSettings = event.context?.personas?.audience_settings;
        }
        return action.execute({
            mapping,
            data: event,
            settings,
            audienceSettings,
            auth,
            features,
            statsContext,
            logger,
            engageDestinationCache,
            transactionContext,
            stateContext,
            subscriptionMetadata
        });
    }
    async executeBatch(actionSlug, { events, mapping, subscriptionMetadata, settings, auth, features, statsContext, logger, engageDestinationCache, transactionContext, stateContext }) {
        const action = this.actions[actionSlug];
        if (!action) {
            return [];
        }
        let audienceSettings = {};
        if (events[0].context?.personas) {
            audienceSettings = events[0].context?.personas?.audience_settings;
        }
        return await action.executeBatch({
            mapping,
            data: events,
            settings,
            audienceSettings,
            auth,
            features,
            statsContext,
            logger,
            engageDestinationCache,
            transactionContext,
            stateContext,
            subscriptionMetadata
        });
    }
    async executeDynamicField(actionSlug, fieldKey, data, dynamicFn) {
        const action = this.actions[actionSlug];
        if (!action) {
            return [];
        }
        return action.executeDynamicField(fieldKey, data, dynamicFn);
    }
    async onSubscription(subscription, events, settings, auth, options) {
        const isBatch = Array.isArray(events);
        const subscriptionStartedAt = (0, time_1.time)();
        const actionSlug = subscription.partnerAction;
        const input = {
            mapping: subscription.mapping || {},
            subscriptionMetadata: {
                actionConfigId: subscription.ID,
                destinationConfigId: subscription.ConfigID,
                actionId: subscription.ActionID,
                sourceId: subscription.ProjectID
            },
            settings,
            auth,
            features: options?.features || {},
            statsContext: options?.statsContext || {},
            logger: options?.logger,
            engageDestinationCache: options?.engageDestinationCache,
            transactionContext: options?.transactionContext,
            stateContext: options?.stateContext
        };
        let results = null;
        try {
            if (!subscription.subscribe || typeof subscription.subscribe !== 'string') {
                const response = {
                    status: 400,
                    errortype: errors_1.ErrorCodes.PAYLOAD_VALIDATION_FAILED,
                    errormessage: 'Failed to validate subscription',
                    errorreporter: errors_1.MultiStatusErrorReporter.INTEGRATIONS
                };
                if (isBatch) {
                    options?.statsContext?.statsClient?.incr('action.multistatus_discard', events.length, options.statsContext?.tags);
                    return [
                        {
                            multistatus: Array(events.length).fill(response)
                        }
                    ];
                }
                return [{ output: response.errormessage }];
            }
            const parsedSubscription = (0, destination_subscriptions_1.parseFql)(subscription.subscribe);
            if (parsedSubscription.error) {
                const response = {
                    status: 400,
                    errortype: errors_1.ErrorCodes.PAYLOAD_VALIDATION_FAILED,
                    errormessage: `Invalid subscription : ${parsedSubscription.error.message}`,
                    errorreporter: errors_1.MultiStatusErrorReporter.INTEGRATIONS
                };
                if (isBatch) {
                    options?.statsContext?.statsClient?.incr('action.multistatus_discard', events.length, options.statsContext?.tags);
                    return [
                        {
                            multistatus: Array(events.length).fill(response)
                        }
                    ];
                }
                return [{ output: response.errormessage }];
            }
            const allEvents = isBatch ? events : [events];
            const subscribedEvents = [];
            const multistatus = [];
            const invalidPayloadIndices = new Set();
            for (let i = 0; i < allEvents.length; i++) {
                const event = allEvents[i];
                if (!(0, destination_subscriptions_1.validate)(parsedSubscription, event)) {
                    multistatus[i] = {
                        status: 400,
                        errortype: errors_1.ErrorCodes.PAYLOAD_VALIDATION_FAILED,
                        errormessage: 'Payload is either invalid or does not match the subscription',
                        errorreporter: errors_1.MultiStatusErrorReporter.INTEGRATIONS
                    };
                    invalidPayloadIndices.add(i);
                    options?.statsContext?.statsClient?.incr('action.multistatus_discard', 1, options.statsContext?.tags);
                    continue;
                }
                subscribedEvents.push(event);
            }
            if (subscribedEvents.length === 0) {
                results = [{ output: 'not subscribed' }];
                return results;
            }
            else if (isBatch) {
                const executeBatchResponse = await this.executeBatch(actionSlug, { ...input, events: subscribedEvents });
                let mergeIndex = 0;
                for (let i = 0; i < allEvents.length; i++) {
                    if (invalidPayloadIndices.has(i)) {
                        continue;
                    }
                    multistatus[i] = executeBatchResponse[mergeIndex++];
                }
                return [{ multistatus }];
            }
            else {
                return await this.executeAction(actionSlug, { ...input, event: subscribedEvents[0] });
            }
        }
        catch (err) {
            const error = err;
            results = [{ error: { message: error.message } }];
            if (error.name === 'AggregateAjvError' || error.name === 'ValidationError') {
                error.status = 400;
            }
            throw error;
        }
        finally {
            const subscriptionEndedAt = (0, time_1.time)();
            const subscriptionDuration = (0, time_1.duration)(subscriptionStartedAt, subscriptionEndedAt);
            options?.onComplete?.({
                duration: subscriptionDuration,
                destination: this.name,
                action: actionSlug,
                subscribe: subscription.subscribe,
                input: {
                    data: events,
                    mapping: input.mapping,
                    settings: input.settings
                },
                output: results
            });
        }
    }
    onEvent(event, settings, options) {
        return this.onSubscriptions(event, settings, options);
    }
    onBatch(events, settings, options) {
        return this.onSubscriptions(events, settings, options);
    }
    async _onDelete(event, settings, options) {
        const { userId, anonymousId } = event;
        const payload = { userId, anonymousId };
        const destinationSettings = this.getDestinationSettings(settings);
        this.validateSettings(destinationSettings);
        const run = async () => {
            const auth = (0, parse_settings_1.getAuthData)(settings);
            const data = {
                payload,
                settings: destinationSettings,
                auth
            };
            const context = {
                settings: destinationSettings,
                payload,
                auth
            };
            const opts = this.extendRequest?.(context) ?? {};
            const requestClient = (0, create_request_client_1.default)({ ...opts, statsContext: context.statsContext });
            const deleteResult = await this.definition.onDelete?.(requestClient, data);
            const result = deleteResult ?? { output: 'no onDelete defined' };
            return result;
        };
        const onFailedAttempt = async (error) => {
            settings = await this.handleError(error, settings, options);
        };
        return await (0, retry_1.retry)(run, { retries: 2, onFailedAttempt });
    }
    async onSubscriptions(data, settings, options) {
        const subscriptions = this.getSubscriptions(settings);
        const destinationSettings = this.getDestinationSettings(settings);
        this.validateSettings(destinationSettings);
        const run = async () => {
            const authData = (0, parse_settings_1.getAuthData)(settings);
            const promises = subscriptions.map((subscription) => this.onSubscription(subscription, data, destinationSettings, authData, options));
            const results = await Promise.all(promises);
            return [].concat(...results);
        };
        const MAX_ATTEMPTS = 2;
        const onFailedAttempt = async (error) => {
            settings = await this.handleError(error, settings, options);
        };
        const shouldRetry = async (response, attemptCount) => {
            const results = response;
            for (const result of results) {
                const has401Errors = result?.multistatus?.some((event) => event.status === 401);
                const isOAuthDestination = this.authentication?.scheme === 'oauth2' || this.authentication?.scheme === 'oauth-managed';
                if (attemptCount <= MAX_ATTEMPTS && has401Errors && isOAuthDestination) {
                    await this.handleAuthError(settings, options);
                    return false;
                }
            }
            return true;
        };
        return await (0, retry_1.retry)(run, { retries: MAX_ATTEMPTS, onFailedAttempt, shouldRetry });
    }
    getSubscriptions(settings) {
        const { subscription, subscriptions } = settings;
        let parsedSubscriptions;
        if (subscription) {
            parsedSubscriptions = [subscription];
        }
        else if (Array.isArray(subscriptions)) {
            parsedSubscriptions = subscriptions;
        }
        else {
            parsedSubscriptions = [];
        }
        return parsedSubscriptions;
    }
    getDestinationSettings(settings) {
        const { subcription, subscriptions, oauth, ...otherSettings } = settings;
        return otherSettings;
    }
    async handleError(error, settings, options) {
        const statusCode = error.status ?? error?.response?.status ?? 500;
        const needsReauthentication = statusCode === 401 &&
            (this.authentication?.scheme === 'oauth2' || this.authentication?.scheme === 'oauth-managed');
        if (!needsReauthentication) {
            throw error;
        }
        return this.handleAuthError(settings, options);
    }
    async handleAuthError(settings, options) {
        const newTokens = await this.refreshTokenAndGetNewToken(settings, options);
        await options?.onTokenRefresh?.(newTokens);
        settings = (0, parse_settings_1.updateOAuthSettings)(settings, newTokens);
        return settings;
    }
    async refreshTokenAndGetNewToken(settings, options) {
        const destinationSettings = this.getDestinationSettings(settings);
        const oauthSettings = (0, parse_settings_1.getOAuth2Data)(settings);
        const newTokens = await this.refreshAccessToken(destinationSettings, oauthSettings, options?.synchronizeRefreshAccessToken);
        if (!newTokens) {
            throw new errors_1.InvalidAuthenticationError('Failed to refresh access token', errors_1.ErrorCodes.OAUTH_REFRESH_FAILED);
        }
        return newTokens;
    }
}
exports.Destination = Destination;
//# sourceMappingURL=index.js.map